import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { API } from "../../constants/API";

export interface SegItem {
  _id: string;
  EAN: number;
  SEASON: string;
  PAPER_CUT_OFF?: string;
  GROUP_1: string;
  TESTIMPRINTFROMHNA: string;
  FULL_TITLE: string;
  AUTHOR_1: string;
  PUB_DATE: string;
  RELEASE_DATE: string;
  FORMAT: string;
  LTD_GROSS_UNITS: string;
  US_PRICE: number;
  bksnPos: string;
}

interface sortObj {
  heading: string;
  sortType: string;
}

interface SegState {
  data: SegItem[];
  status: string;
  hasMore: boolean;
  statusCode: number;
  error: string | null;
  isLoading: boolean;
  page: number;
  prevPage: number;
  limit: number;
  selectedRowIndex: number | any;
  titleSortBy: sortObj;
  scrolling: boolean;
  // New state properties for OTIS updates
  finalData: any[];
  submitButtonDisabled: boolean;
  updateOtis: {
    isLoading: boolean;
    message: string;
    status: number | null;
  };
}

const initialState: SegState = {
  data: [],
  page: 1,
  limit: 15,
  status: "",
  hasMore: false,
  error: null,
  statusCode: 0,
  isLoading: false,
  selectedRowIndex: 0,
  prevPage: 1,
  scrolling: false,
  titleSortBy: {
    heading: "EAN",
    sortType: "desc",
  },
  // Initialize new state properties
  finalData: [],
  submitButtonDisabled: false,
  updateOtis: {
    isLoading: false,
    message: "",
    status: null,
  },
};

// Async thunk for fetching SEG records
export const FetchSegRecord = createAsyncThunk(
  "SegData/FetchSegRecord",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.POSTSEGDATA, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

// Async thunk for updating OTIS records
export const updateSelectedOTISRecord = createAsyncThunk(
  "SegData/updateSelectedOTISRecord",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(API.UPDATEOTISRECORD, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }
);

const Segslice = createSlice({
  name: "SegData",
  initialState,
  reducers: {
    emptySEGMainPage: (state) => {
      state.page = initialState.page;
      state.data = initialState.data;
      state.selectedRowIndex = initialState.selectedRowIndex;
      state.prevPage = initialState.prevPage;
      state.status = initialState.status;
      state.statusCode = initialState.statusCode;
      state.error = initialState.error;
      state.isLoading = initialState.isLoading;
      state.limit = initialState.limit;
    },
    UpdatePagination: (state) => {
      state.page = state.page + 1;
    },
    setscrolling: (state, action) => {
      state.scrolling = action.payload;
    },
    emptyPreviousSegRecords: (state) => {
      state.data = initialState.data;
    },
    setSelectedRowIndex: (state, action) => {
      state.selectedRowIndex = action.payload;
    },
    updatePrevPage: (state, action) => {
      state.prevPage = action.payload;
    },
    titleListSortByDesc: (state, action) => {
      state.titleSortBy = action.payload;
    },
    resetSEGPage: (state) => {
      state.page = initialState.page;
    },
    // New reducer for OTIS updates
    setFinalData: (state, action) => {
      state.finalData = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      // Handle FetchSegRecord
      .addCase(FetchSegRecord.pending, (state) => {
        state.isLoading = true;
        state.status = "Loading";
      })
      .addCase(FetchSegRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = "success";
        state.hasMore = action.payload.hasMore;
        if (action.payload.filteredNew || state.page === 1) {
          state.data = action.payload.SEGs;
          state.page = 1;
          state.selectedRowIndex = 0;
        } else {
          state.data.push(...action.payload.SEGs);
        }
      })
      .addCase(FetchSegRecord.rejected, (state) => {
        state.isLoading = false;
        state.status = "Failed";
        state.error = "Error";
      })
      // Handle updateSelectedOTISRecord
      .addCase(updateSelectedOTISRecord.pending, (state) => {
        state.submitButtonDisabled = true;
        state.updateOtis.isLoading = true;
      })
      .addCase(updateSelectedOTISRecord.fulfilled, (state, action) => {
        state.updateOtis.isLoading = false;
        state.updateOtis.message = action.payload.data?.message ?? "Success";
        state.updateOtis.status = action.payload.status;
        state.submitButtonDisabled = false;
      })
      .addCase(updateSelectedOTISRecord.rejected, (state, action: PayloadAction<any>) => {
        state.updateOtis.isLoading = false;
        state.updateOtis.status = action.payload.status;
        state.submitButtonDisabled = false;
        state.updateOtis.message = "Failed to update OTIS record";
      });
  },
});

export const {
  emptySEGMainPage,
  resetSEGPage,
  setscrolling,
  titleListSortByDesc,
  UpdatePagination,
  emptyPreviousSegRecords,
  setSelectedRowIndex,
  updatePrevPage,
  setFinalData,
} = Segslice.actions;

export default Segslice.reducer;